import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, InputBase, IconButton, Button, Switch, Tooltip } from '@mui/material';
import { ContentCopyIcon, InfoOutlinedIcon } from 'icons/materialIcons';
import { useNavigate } from 'react-router-dom';
import { getUserById } from 'apis/authApi';
import { useUserContext } from 'contexts/UserContext';
import { useUserPreferences } from 'contexts/UserPreferencesContext';
import { useAuth } from 'contexts/AuthContext';
import NotificationSnackbar from 'components/common/notification/NotificationSnackbar';

const SettingsContainer = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null); // State to store user data
  const { userState } = useUserContext();
  const { authState } = useAuth();
  const userId = authState.user.userId; // Get userId from authState
  const userName = userState.userName; // Access userName from userState
  const [isInitialized, setIsInitialized] = useState(false);

  // Get user preferences
  const { getDefaultFollowUpEmail, setDefaultFollowUpEmail } = useUserPreferences();
  // State for form fields - COMMENTED OUT FOR FUTURE IMPLEMENTATION
  // const [profileUrl, setProfileUrl] = useState('');
  // const [email, setEmail] = useState('');
  // const [subdomain, setSubdomain] = useState('');

  // State for toggles
  const [isFollowUpEmailEnabled, setIsFollowUpEmailEnabled] = useState(false);
  // const [isRemoveBrandingEnabled, setIsRemoveBrandingEnabled] = useState(false); // COMMENTED OUT FOR FUTURE IMPLEMENTATION

  // Notification state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  // Track initial values to compare with current ones - COMMENTED OUT FOR FUTURE IMPLEMENTATION
  // const [initialProfileUrl, setInitialProfileUrl] = useState('');
  // const [initialEmail, setInitialEmail] = useState('');
  // const [initialSubdomain, setInitialSubdomain] = useState('');

  // Log for debugging
  useEffect(() => {
    console.log("[SettingsContainer] Using userId from authState:", userId);
  }, [userId]);

  // Load the follow-up email preference when component mounts
  useEffect(() => {
    if (userId) {
      const preference = getDefaultFollowUpEmail();
      console.log(`[SettingsContainer] Loaded follow-up email preference: ${preference}`);
      setIsFollowUpEmailEnabled(preference);
    }
  }, [userId, getDefaultFollowUpEmail]);
  // COMMENTED OUT FOR FUTURE IMPLEMENTATION
  // useEffect(() => {
  //   // Set initial values when the component mounts
  //   setInitialProfileUrl(profileUrl);
  //   setInitialEmail(email);
  //   setInitialSubdomain(subdomain);
  // }, [profileUrl, email, subdomain]);

  // Handler for input changes - COMMENTED OUT FOR FUTURE IMPLEMENTATION
  // const handleInputChange = (e, setter) => {
  //   setter(e.target.value);
  // };

  // Handler for Follow Up Email toggle - updated to use context
  const handleFollowUpEmailToggle = useCallback((event) => {
    const isEnabled = event.target.checked;
    console.log(`[SettingsContainer] Toggle follow-up email to: ${isEnabled}`);

    // Update local state
    setIsFollowUpEmailEnabled(isEnabled);

    // Update in context
    setDefaultFollowUpEmail(isEnabled);

    // Show success message
    setSnackbar({
      open: true,
      message: isEnabled
        ? 'Follow-up emails will be enabled by default for new cards'
        : 'Follow-up emails will be disabled by default for new cards',
      severity: 'success'
    });
  }, [setDefaultFollowUpEmail]);
  // Handler for Remove Branding toggle - COMMENTED OUT FOR FUTURE IMPLEMENTATION
  // const handleRemoveBrandingToggle = useCallback((event) => {
  //   const isEnabled = event.target.checked;
  //   setIsRemoveBrandingEnabled(isEnabled);
  //   console.log('Remove branding state:', isEnabled);
  // }, []);

  // Check if any changes have been made - COMMENTED OUT FOR FUTURE IMPLEMENTATION
  // const isFormModified =
  //   profileUrl !== initialProfileUrl ||
  //   email !== initialEmail ||
  //   subdomain !== initialSubdomain;

  // Handler for Custom Subdomain click - COMMENTED OUT FOR FUTURE IMPLEMENTATION
  // const handleSubdomainClick = () => {
  //   navigate('/');
  // };
  useEffect(() => {
    // Only fetch user data once when component mounts - COMMENTED OUT FOR FUTURE IMPLEMENTATION
    // const fetchUserData = async () => {
    //   if (isInitialized || !userId) return;

    //   try {
    //     const idToken = localStorage.getItem('authToken'); // Retrieve the auth token from local storage
    //     if (!idToken) {
    //       throw new Error('No auth token found');
    //     }

    //     const response = await getUserById(userId, idToken);
    //     setUserData(response.data);
    //     console.log('Fetched user data from EditCardNavbar:', response.data.fullName); // Console log the user data

    //     // Set the email from the user data
    //     if (response.data.email) {
    //       setEmail(response.data.email);
    //     }

    //     setIsInitialized(true);
    //   } catch (error) {
    //     console.error('Error fetching user data:', error.message);
    //   }
    // };

    // fetchUserData();
  }, [userId, isInitialized]);

  return (
    <Box
      sx={{
        backgroundColor: '#fff', // White background for the entire container
        minHeight: '95vh', // Occupies most of the viewport height
        maxWidth: '100%', // Maximum width for larger screens
        margin: '5px 80px', // Margins on the sides
        px: 8, // Padding on the sides
        py: 1, // Padding on the top and bottom
        borderRadius: 4, // Rounded corners for a smooth look
        boxSizing: 'border-box', // Ensures padding is included in width/height
      }}
    >

      {/* Account Settings Container */}
      <Box
        sx={{
          width: '100%', // Ensures it spans the full parent width
          padding: 4, // Padding around the container
          boxSizing: 'border-box', // Ensures padding doesn't affect the width calculation
          marginBottom: 3, // Margin below the container
        }}
      >
        <Typography
          variant="body2"
          sx={{
            textAlign: 'left',
            fontWeight: '500',
            marginBottom: 3,
            fontSize: '1.2rem',
          }}
        >
          Account Settings
        </Typography>        {/* Profile URL Section - COMMENTED OUT FOR FUTURE IMPLEMENTATION */}
        {/* <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
          <Typography
            variant="body1"
            sx={{
              fontSize: '0.8rem',
              fontWeight: 600,
              color: '#4f4f4f',
              marginRight: 1, // Margin between label and icon
              cursor: 'pointer', // Change cursor on hover over the label
            }}
          >
            Profile URL
          </Typography>

          <IconButton
            sx={{
              fontWeight: 600,
              color: '#828282',
              padding: 0, // Remove padding around icon
              cursor: 'pointer', // Change cursor on hover over the icon
            }}
          >
            <ContentCopyIcon sx={{ fontSize: 12 }} /> {/* Apply fontSize directly to the icon */}
        {/* </IconButton>
        </Box> */}

        {/* Profile URL Input - COMMENTED OUT FOR FUTURE IMPLEMENTATION */}
        {/* <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            backgroundColor: '#f7f7f7', // Light background for the input field
            padding: '8px 12px', // Padding inside the input field
            borderRadius: 2, // Rounded corners for the input
            width: '100%', // Full width of the parent container
          }}
        >
          <InputBase
            sx={{
              flex: 1, // Makes the input field take up the remaining space
              padding: '3px',
              fontSize: '0.8rem',
              color: '#707070',
              borderRadius: 4,
              backgroundColor: '#f7f7f7',
              border: 'none', // No border for the input
              outline: 'none', // No outline on focus
            }}
            placeholder="Enter your profile URL"
            value={profileUrl}
            onChange={(e) => handleInputChange(e, setProfileUrl)}
          />
        </Box> */}

        {/* Email Section - COMMENTED OUT FOR FUTURE IMPLEMENTATION */}
        {/* <Box sx={{ display: 'flex', alignItems: 'center', marginTop: 3, marginBottom: 2 }}>
          <Typography
            variant="body1"
            sx={{
              fontSize: '0.8rem',
              fontWeight: 600,
              color: '#4f4f4f',
              marginRight: 1, // Margin between label and icon
              cursor: 'pointer', // Change cursor on hover over the label
            }}
          >
            Email
          </Typography>
        </Box> */}

        {/* Email Input - COMMENTED OUT FOR FUTURE IMPLEMENTATION */}
        {/* <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            backgroundColor: '#f7f7f7', // Light background for the input field
            padding: '8px 12px', // Padding inside the input field
            borderRadius: 2, // Rounded corners for the input
            width: '100%', // Full width of the parent container
          }}
        >
          <InputBase
            sx={{
              flex: 1, // Makes the input field take up the remaining space
              padding: '3px',
              fontSize: '0.8rem',
              color: '#707070',
              borderRadius: 4,
              backgroundColor: '#f7f7f7',
              border: 'none', // No border for the input
              outline: 'none', // No outline on focus
            }}
            placeholder="Enter your email address"
            value={email}
            onChange={(e) => handleInputChange(e, setEmail)}
          />
        </Box> */}

        {/* Custom Subdomain Section - COMMENTED OUT FOR FUTURE IMPLEMENTATION */}
        {/* <Box sx={{ display: 'flex', alignItems: 'center', marginTop: 3, marginBottom: 2 }}>
          <Typography
            variant="body1"
            sx={{
              fontSize: '0.8rem',
              fontWeight: 600,
              color: '#4f4f4f',
              marginRight: 1, // Margin between label and input
              cursor: 'pointer', // Change cursor on hover over the label
            }}
            onClick={handleSubdomainClick} // Navigate to '/' on click
          >
            Custom Subdomain
          </Typography>

          <Typography
            variant="body1"
            sx={{
              fontSize: '0.6rem',
              fontWeight: 500,
              color: '#4f4f4f',
              marginRight: 1, // Margin between label and input
              cursor: 'pointer', // Change cursor on hover over the label
            }}
            onClick={handleSubdomainClick} // Navigate to '/' on click
          >
            Pro+
          </Typography>


        </Box> */}

        {/* Custom Subdomain Input - COMMENTED OUT FOR FUTURE IMPLEMENTATION */}
        {/* <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            backgroundColor: '#f7f7f7', // Light background for the input field
            padding: '8px 12px', // Padding inside the input field
            borderRadius: 2, // Rounded corners for the input
            width: '100%', // Full width of the parent container
          }}
        >
          <InputBase
            sx={{
              flex: 1, // Makes the input field take up the remaining space
              padding: '3px',
              fontSize: '0.8rem',
              color: '#707070',
              borderRadius: 4,
              backgroundColor: '#f7f7f7',
              border: 'none', // No border for the input
              outline: 'none', // No outline on focus
            }}
            placeholder="Enter your custom subdomain"
            disabled // Disable the input field
          />
        </Box> */}



        {/* Buttons: Cancel and Save - COMMENTED OUT FOR FUTURE IMPLEMENTATION */}
        {/* <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 3 }}>
          <Button
            variant="contained"
            sx={{
              borderRadius: 4,
              fontSize: '0.8rem',
              color: isFormModified ? '#000000' : '#a6a6a6', // Gray background if no changes

              backgroundColor: isFormModified ? '#ffffff' : '#e0e0e0', // Gray background if no changes              cursor: isFormModified ? 'default' : 'pointer', // Disable clicking if no changes
              fontWeight: 600,
              textTransform: 'none', // Prevent uppercase text transformation
              marginRight: 2, // Space between buttons
              padding: '6px 32px', // Padding inside the button
              border: isFormModified ? '1px solid #dedede' : 'none', // Conditional border

              boxShadow: 'none', // Remove shadow effect
            }}
            onClick={() => console.log('Cancel clicked')} // Placeholder for cancel action
            disabled={!isFormModified} // Disable the button if no changes
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            sx={{
              backgroundColor: '#e0e0e0',
              borderRadius: 4,
              fontWeight: 600,

              fontSize: '0.8rem',
              textTransform: 'none', // Prevent uppercase text transformation
              padding: '6px 32px', // Padding inside the button
              cursor: 'pointer', // Change cursor on hover
              boxShadow: 'none', // Remove shadow effect
              color: isFormModified ? '#ffffff' : '#a6a6a6', // Gray background if no changes

              backgroundColor: isFormModified ? '#000000' : '#e0e0e0', // Gray background if no changes

            }}
            onClick={() => console.log('Save clicked')} // Placeholder for save action
            disabled={!isFormModified} // Disable if no changes

          >
            Save
          </Button>


        </Box> */}

        {/* <Box sx={{ width: '100%', borderBottom: '1px solid #e0e0e0', marginTop: 3 }} /> */}





        {/* Follow Up Email Section */}
        <Box sx={{ marginTop: 3, marginBottom: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography
              variant="body1"
              sx={{
                fontSize: '0.8rem',
                fontWeight: 600,
                color: '#4f4f4f',
              }}
            >
              Follow Up Email
            </Typography>
            <Tooltip
              title="This setting controls the default for new cards. Individual cards can override this setting."
              placement="top"
            >
              <InfoOutlinedIcon
                sx={{
                  fontSize: '16px',
                  marginLeft: 1,
                  color: '#9e9e9e',
                  cursor: 'help'
                }}
              />
            </Tooltip>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              padding: '8px 0',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.75rem',
                color: '#828282',
                fontWeight: 600,
                lineHeight: 1.5,
                maxWidth: '50%',
              }}
            >
              Automatically send an email connecting the user of this profile and their new connection upon exchanging info. This will be the default for new cards.
            </Typography>

            {/* Material-UI Switch Component */}
            <Switch
              checked={isFollowUpEmailEnabled}
              onChange={handleFollowUpEmailToggle}
              color="primary"
            />
          </Box>        </Box>

        {/* <Box sx={{ width: '100%', borderBottom: '1px solid #e0e0e0', marginTop: 2 }} /> */}

        {/* Remove Touch Branding Section - COMMENTED OUT FOR FUTURE IMPLEMENTATION */}
        {/* <Box sx={{ marginTop: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography
              variant="body1"
              sx={{
                fontSize: '0.8rem',
                fontWeight: 600,
                color: '#4f4f4f',
              }}
            >
              Remove Touch Branding
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: '0.6rem',
                fontWeight: 500,
                color: '#4f4f4f',
                marginLeft: 1, // Margin between label and input
              }}
            >
              Pro
            </Typography>
          </Box>


          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              padding: '8px 0',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.75rem',
                color: '#828282',
                fontWeight: 600,
                lineHeight: 1.5,
                maxWidth: '50%',
              }}
            >
              Remove Pro logo and other Touch branding from your account
            </Typography>

            <Switch
              checked={isRemoveBrandingEnabled}
              onChange={handleRemoveBrandingToggle}
              color="primary"
            />
          </Box>
        </Box> */}
      </Box>

      {/* Notification Snackbar */}
      <NotificationSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        handleClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default SettingsContainer;